import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Select,
  MenuItem,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Divider,
  Switch,
  Stack,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";
import {
  StoredPreferences,
  UsagePlan,
  loadStoredPreferences,
  saveStoredPreference,
} from "../../utils/preferences";

interface Preference {
  key: keyof StoredPreferences;
  label: string;
  value: string; // Display value (localized)
  code: string; // Stable code for storage
  options: Array<{ code: string; label: string }>;
}

// Code to localized label mapping
const DATE_FORMAT_OPTIONS = [
  { code: "yyyy_mm_dd", labelKey: "settings.preferences.formats.yyyyMmDd" },
  { code: "dd_mm_yyyy", labelKey: "settings.preferences.formats.ddMmYyyy" },
  { code: "mm_dd_yyyy", labelKey: "settings.preferences.formats.mmDdYyyy" },
];

const TIME_FORMAT_OPTIONS = [
  { code: "h24", labelKey: "settings.preferences.formats.24Hours" },
  { code: "h12", labelKey: "settings.preferences.formats.12Hours" },
];

const CURRENCY_OPTIONS = [
  { code: "usd", labelKey: "settings.preferences.currencies.usd" },
  { code: "eur", labelKey: "settings.preferences.currencies.eur" },
  { code: "gbp", labelKey: "settings.preferences.currencies.gbp" },
  { code: "try", labelKey: "settings.preferences.currencies.try" },
];

const LANGUAGE_OPTIONS = [
  { code: "en", labelKey: "settings.preferences.languages.en" },
  { code: "tr", labelKey: "settings.preferences.languages.tr" },
];


const PreferencesPage: React.FC = () => {
  const { t } = useTranslation();
  const [storedPreferences, setStoredPreferences] = useState<StoredPreferences>(
    () => loadStoredPreferences()
  );
  const [preferences, setPreferences] = useState<Preference[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editCode, setEditCode] = useState<string>("");

  // Load preferences from localStorage and build UI state
  useEffect(() => {
    const normalizedPrefs = loadStoredPreferences();
    setStoredPreferences(normalizedPrefs);

    if (
      normalizedPrefs.language &&
      normalizedPrefs.language !== i18n.language
    ) {
      i18n.changeLanguage(normalizedPrefs.language);
    }

    const prefs: Preference[] = [
      {
        key: "dateFormat",
        label: t("settings.preferences.dateFormat"),
        code: normalizedPrefs.dateFormat,
        value: t(
          DATE_FORMAT_OPTIONS.find(
            (option) => option.code === normalizedPrefs.dateFormat
          )?.labelKey || "settings.preferences.formats.yyyyMmDd"
        ),
        options: DATE_FORMAT_OPTIONS.map((option) => ({
          code: option.code,
          label: t(option.labelKey),
        })),
      },
      {
        key: "timeFormat",
        label: t("settings.preferences.timeFormat"),
        code: normalizedPrefs.timeFormat,
        value: t(
          TIME_FORMAT_OPTIONS.find(
            (option) => option.code === normalizedPrefs.timeFormat
          )?.labelKey || "settings.preferences.formats.24Hours"
        ),
        options: TIME_FORMAT_OPTIONS.map((option) => ({
          code: option.code,
          label: t(option.labelKey),
        })),
      },
      {
        key: "currency",
        label: t("settings.preferences.currency"),
        code: normalizedPrefs.currency,
        value: t(
          CURRENCY_OPTIONS.find(
            (option) => option.code === normalizedPrefs.currency
          )?.labelKey || "settings.preferences.currencies.usd"
        ),
        options: CURRENCY_OPTIONS.map((option) => ({
          code: option.code,
          label: t(option.labelKey),
        })),
      },
      {
        key: "language",
        label: t("settings.preferences.language"),
        code: normalizedPrefs.language,
        value: t(
          LANGUAGE_OPTIONS.find(
            (option) => option.code === normalizedPrefs.language
          )?.labelKey || "settings.preferences.languages.en"
        ),
        options: LANGUAGE_OPTIONS.map((option) => ({
          code: option.code,
          label: t(option.labelKey),
        })),
      },
    ];

    setPreferences(prefs);
  }, [t]);

  const handleEditClick = (index: number) => {
    setEditingIndex(index);
    setEditCode(preferences[index].code);
  };

  const handleSaveClick = () => {
    if (editingIndex !== null) {
      const prefKey = preferences[editingIndex].key;
      const validCodes = preferences[editingIndex].options.map(
        (option) => option.code
      );
      const newCode = validCodes.includes(editCode)
        ? editCode
        : preferences[editingIndex].code;

      saveStoredPreference(prefKey, newCode as StoredPreferences[typeof prefKey]);
      setStoredPreferences((prev) => ({ ...prev, [prefKey]: newCode }));

      // Special handling for language - change i18n language immediately
      if (prefKey === "language" && newCode !== i18n.language) {
        i18n.changeLanguage(newCode);
      }

      // Update UI state
      const updatedPreferences = [...preferences];
      updatedPreferences[editingIndex].code = newCode;
      const selectedOption = updatedPreferences[editingIndex].options.find(o => o.code === newCode);
      if (selectedOption) {
        updatedPreferences[editingIndex].value = selectedOption.label;
      }
      setPreferences(updatedPreferences);

      setEditingIndex(null);
      setEditCode("");
    }
  };

  const handleUsagePlanToggle = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const nextUsagePlan: UsagePlan = event.target.checked
      ? "premium"
      : "freemium";

    saveStoredPreference("usagePlan", nextUsagePlan);
    setStoredPreferences((prev) => ({ ...prev, usagePlan: nextUsagePlan }));
  };

  const handleCancelClick = () => {
    setEditingIndex(null);
    setEditCode("");
  };

  return (
    <Box sx={{ margin: "auto", padding: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {t("settings.preferences.title")}
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        {t("settings.preferences.description")}
      </Typography>
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={10}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="body1" sx={{ color: "gray" }}>
                {t("settings.preferences.usagePlan")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("settings.preferences.usagePlanDescription")}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography
                variant="body2"
                color={
                  storedPreferences.usagePlan === "freemium"
                    ? "text.primary"
                    : "text.secondary"
                }
              >
                {t("settings.preferences.usagePlans.freemium")}
              </Typography>
              <Switch
                checked={storedPreferences.usagePlan === "premium"}
                onChange={handleUsagePlanToggle}
                inputProps={{ "aria-label": t("settings.preferences.usagePlan") }}
              />
              <Typography
                variant="body2"
                color={
                  storedPreferences.usagePlan === "premium"
                    ? "text.primary"
                    : "text.secondary"
                }
              >
                {t("settings.preferences.usagePlans.premium")}
              </Typography>
            </Stack>
          </Box>
          <Divider />
        </Grid>
        {preferences.map((preference, index) => (
          <Grid item xs={10} key={preference.label}>
            {editingIndex === index ? (
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>{preference.label}</InputLabel>
                  <Select
                    value={editCode}
                    onChange={(e) => setEditCode(e.target.value)}
                    label={preference.label}
                  >
                    {preference.options.map((option) => (
                      <MenuItem key={option.code} value={option.code}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  onClick={handleSaveClick}
                  variant="contained"
                  color="primary"
                  sx={{ ml: 2 }}
                >
                  {t("common.save")}
                </Button>
                <Button
                  onClick={handleCancelClick}
                  variant="outlined"
                  color="secondary"
                  sx={{ ml: 1 }}
                >
                  {t("common.cancel")}
                </Button>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="body1" sx={{ color: "gray" }}>
                  {preference.label}
                </Typography>
                <Typography variant="body1">{preference.value}</Typography>
                <IconButton
                  edge="end"
                  aria-label="edit"
                  onClick={() => handleEditClick(index)}
                >
                  <EditIcon color="primary" />
                </IconButton>
              </Box>
            )}
            <Divider />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default PreferencesPage;
